/**
 * Schema Viewer Modal
 * Displays database schema in a code-like viewer with syntax highlighting
 */

import { X, Clock, Code2, Database } from 'lucide-react';
import Modal from '../shared/Modal';

interface SchemaViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  schema: any;
  dataSourceName: string;
  schemaHistory?: Array<{
    version: number;
    schema: any;
    pulledAt: Date;
    changes?: string;
  }>;
}

const SchemaViewerModal = ({
  isOpen,
  onClose,
  schema,
  dataSourceName,
  schemaHistory = [],
}: SchemaViewerModalProps) => {
  if (!schema) return null;

  const renderPostgreSQLSchema = () => {
    if (!schema.tables) return null;

    return (
      <div className="space-y-4">
        <div className="text-sm text-secondary-600">
          {schema.tables.length} Tables • {schema.database}
        </div>

        {schema.tables.map((table: any, idx: number) => (
          <div
            key={idx}
            className="bg-secondary-50 dark:bg-secondary-800 rounded-lg p-4 font-mono text-sm"
          >
            <div className="flex items-center gap-2 mb-3">
              <Database className="w-4 h-4 text-primary-600" />
              <span className="font-bold text-primary-700 dark:text-primary-300">
                {table.tableName}
              </span>
              <span className="text-xs text-secondary-500">
                ({table.columns.length} columns)
              </span>
            </div>

            <div className="space-y-1 ml-6">
              {table.columns.map((col: any, cidx: number) => (
                <div key={cidx} className="flex items-start gap-2 text-xs">
                  <span className="text-blue-600 dark:text-blue-400">{col.columnName}</span>
                  <span className="text-purple-600 dark:text-purple-400">{col.dataType}</span>
                  {col.isPrimaryKey && (
                    <span className="px-1.5 py-0.5 bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300 rounded">
                      PK
                    </span>
                  )}
                  {col.isForeignKey && (
                    <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded">
                      FK
                    </span>
                  )}
                  {!col.isNullable && (
                    <span className="text-red-500 dark:text-red-400">NOT NULL</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderMongoDBSchema = () => {
    if (!schema.collections) return null;

    return (
      <div className="space-y-4">
        <div className="text-sm text-secondary-600">
          {schema.collections.length} Collections • {schema.database}
        </div>

        {schema.collections.map((coll: any, idx: number) => (
          <div
            key={idx}
            className="bg-secondary-50 dark:bg-secondary-800 rounded-lg p-4 font-mono text-sm"
          >
            <div className="flex items-center gap-2 mb-3">
              <Database className="w-4 h-4 text-primary-600" />
              <span className="font-bold text-primary-700 dark:text-primary-300">
                {coll.collectionName}
              </span>
              <span className="text-xs text-secondary-500">
                ({coll.totalDocuments.toLocaleString()} documents)
              </span>
            </div>

            <div className="space-y-1 ml-6">
              {coll.detectedFields.map((field: any, fidx: number) => (
                <div key={fidx} className="flex items-start gap-2 text-xs">
                  <span className="text-blue-600 dark:text-blue-400">{field.fieldName}:</span>
                  <span className="text-purple-600 dark:text-purple-400">{field.dataType}</span>
                  <span className="text-green-600 dark:text-green-400">
                    ({field.frequency}%)
                  </span>
                  {field.isArray && (
                    <span className="px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 rounded">
                      Array
                    </span>
                  )}
                  {field.isNested && (
                    <span className="px-1.5 py-0.5 bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 rounded">
                      Nested
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderAPISchema = () => {
    if (!schema.collections || schema.collections.length === 0) return null;

    const apiResponse = schema.collections[0];

    return (
      <div className="space-y-4">
        <div className="text-sm text-secondary-600">
          API Response Structure • {apiResponse.totalDocuments} record(s)
        </div>

        <div className="bg-secondary-50 dark:bg-secondary-800 rounded-lg p-4 font-mono text-sm">
          <div className="space-y-1">
            {apiResponse.detectedFields.map((field: any, idx: number) => (
              <div key={idx} className="flex items-start gap-2 text-xs">
                <span className="text-blue-600 dark:text-blue-400">{field.fieldName}:</span>
                <span className="text-purple-600 dark:text-purple-400">{field.dataType}</span>
                {field.isArray && (
                  <span className="px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 rounded">
                    Array
                  </span>
                )}
                {field.isNested && (
                  <span className="px-1.5 py-0.5 bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 rounded">
                    Object
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Schema: ${dataSourceName}`} size="2xl">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Code2 className="w-5 h-5 text-primary-600" />
            <span className="font-semibold">{schema.type.toUpperCase()} Schema</span>
          </div>
          <div className="text-xs text-secondary-500">
            <Clock className="w-3 h-3 inline mr-1" />
            Pulled: {new Date(schema.pulledAt).toLocaleString()}
          </div>
        </div>

        {/* Business Patterns */}
        {schema.detectedPatterns && (
          <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-4">
            <div className="text-sm font-semibold text-primary-900 dark:text-primary-100 mb-2">
              🧠 Detected Business Patterns:
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {schema.detectedPatterns.hasOrders && (
                <div className="text-green-700 dark:text-green-300">
                  ✅ Orders: {schema.detectedPatterns.ordersTable}
                </div>
              )}
              {schema.detectedPatterns.hasCustomers && (
                <div className="text-green-700 dark:text-green-300">
                  ✅ Customers: {schema.detectedPatterns.customersTable}
                </div>
              )}
              {schema.detectedPatterns.hasUsers && (
                <div className="text-green-700 dark:text-green-300">
                  ✅ Users: {schema.detectedPatterns.usersTable}
                </div>
              )}
              {schema.detectedPatterns.hasProducts && (
                <div className="text-green-700 dark:text-green-300">
                  ✅ Products: {schema.detectedPatterns.productsTable}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Schema Content */}
        <div className="max-h-[500px] overflow-y-auto">
          {schema.type === 'postgresql' || schema.type === 'mysql'
            ? renderPostgreSQLSchema()
            : schema.type === 'mongodb'
            ? renderMongoDBSchema()
            : renderAPISchema()}
        </div>

        {/* Version History */}
        {schemaHistory && schemaHistory.length > 0 && (
          <div className="border-t pt-4">
            <div className="text-sm font-semibold mb-3">Version History:</div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {schemaHistory
                .slice()
                .reverse()
                .map((entry, idx) => (
                  <div
                    key={idx}
                    className="text-xs p-2 bg-secondary-50 dark:bg-secondary-800 rounded"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">Version {entry.version}</span>
                      <span className="text-secondary-500">
                        {new Date(entry.pulledAt).toLocaleString()}
                      </span>
                    </div>
                    {entry.changes && (
                      <div className="text-secondary-600 mt-1">{entry.changes}</div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 border-t pt-4">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SchemaViewerModal;

